<template>
  <VBtn
    v-new-tab="$t('a11y.link.opensInNewTab')"
    v-bind="attrs"
    :class="{
      'action-button': action,
      'sbtn-action': action,
      'link--small': linkSmall,
      'link--medium': linkMedium,
      'link--large': linkLarge,
      'link--simple': linkSimple,
      'rounded-btn': rounded,
      'link': isCustomLink,
    }"
    :variant="computedVariant"
    :nuxt="isLink"
    :to="localizedTo"
  >
    <slot />
    <template v-if="$slots.prepend" #prepend>
      <slot name="prepend" />
    </template>
    <template v-if="$slots.append" #append>
      <slot name="append" />
    </template>
  </VBtn>
</template>

<script lang="ts" setup>
import type { RouteLocationAsPathGeneric, RouteLocationAsRelativeGeneric } from "vue-router"

defineOptions({
  inheritAttrs: false
})

interface Props {
 // Adds .action-button plus .sbtn-action. CMS markup also uses .action-button;
 // .sbtn-action is what global.scss excludes from the CMS-CTA inset focus ring.
 action?: boolean
 linkSmall?: boolean
 linkMedium?: boolean
 linkLarge?: boolean
 linkSimple?: boolean
 rounded?: boolean
 to?: string | RouteLocationAsRelativeGeneric | RouteLocationAsPathGeneric
 variant?: 'elevated' | 'flat' | 'tonal' | 'outlined' | 'text' | 'plain'
}

const props = withDefaults(defineProps<Props>(), {
  action: false,
  linkSmall: false,
  linkMedium: false,
  linkLarge: false,
  linkSimple: false,
  rounded: false,
  to: undefined,
  variant: undefined
})
const attrs = useAttrs()
const isLink = computed(() => !!props.to)

const to = toRef(props, 'to')
const { localizedTo } = useLocalizedTo(to)

const isCustomLink = computed(() => props.linkSmall || props.linkLarge || props.linkMedium)
const isUnderlinedLink = computed(() => isCustomLink.value || (!!attrs.text || attrs.text === ''))
const isText = computed(() => isUnderlinedLink.value || props.linkSimple || props.variant === 'text')
const computedVariant = computed(() => {
  if (props.variant) return props.variant
  return isText.value ? 'text' : 'flat'
})
</script>

<style lang="scss" scoped>
@use '~/assets/scss/settings';
@use 'sass:map';

// Bug 60528: Vuetify gives .v-btn__content `transition: opacity 0.2s`
// (VBtn.css) and drops it to opacity 0 while :loading. An axe scan that samples
// mid-fade sees the white label at partial opacity over a filled button
// (secondary #C93374 blends to ~2.45:1) and reports a contrast violation, even
// though both settled states pass (opacity 1 = 4.99:1; opacity 0 = invisible,
// skipped). Snap the fade so there is no interpolated frame to sample.
//
// NOT gated on .v-btn--loading. Gating it there only suppressed the fade going
// INTO loading: removing that class removes this rule at the same instant, and
// per the CSS transitions spec the browser animates using the after-change
// style — so the label faded back 0 -> 1 over 200ms on the way OUT and axe could
// still sample it. On Join that window is reachable whenever validation fails
// and the button settles back on the same page. Ungated covers both directions.
//
// Safe to ungate: .v-btn--loading is the only Vuetify rule that changes opacity
// on .v-btn__content, so this suppresses nothing else. (`.v-btn--variant-plain`
// opacity is on the button root, a different element.) The shorthand's
// `transform` has no duration, so nothing observable is lost there either.
//
// No user-visible change; the label swap on click is imperceptible.
:deep(.v-btn__content) {
  transition: none;
}

.action-button {
  font-weight : 700;
  text-transform : uppercase;
  box-shadow: none;
  font-size: 1.143rem;
  height: 44px;
  padding: 0 16px;

  &.black-button {
    border-radius: 0;
    background-color: rgb(var(--v-theme-primary));
    color: white;
  }
  &.translucent {
    background-color: white;
    color: rgb(var(--v-theme-primary));
    opacity: 0.75;
  }
}

.link--small {
  font-size: 0.8571rem;
}

.link--simple {
  text-decoration: none;
  text-transform: none;
  font-size:  0.8571rem;
  color: rgb(var(--v-theme-header));
}

.link--medium {
  font-size:  1rem;
}

.link--large {
  font-size: 1.143rem;
}

.rounded-btn {
  border-radius: 6px;
}

.link {
  text-decoration: underline;
}

.link:not(.v-btn--block) {
  min-width: auto;
}

.action-button,
.link,
.v-btn--variant-text:not(.link--simple, .rounded-btn) {
  letter-spacing: 0.0892857143em;
  line-height: 1.5;

  :deep(.v-btn__content) {
    letter-spacing: normal;
    line-height: normal;
    text-indent: 0.0892857143em;
  }
}


// `color` adds a `.bg-*` rule in `vuetify-utilities`; action hover follows it.
@layer vuetify-final.trumps {
  .action-button {
    &.translucent:hover {
      background-color: rgb(var(--v-theme-hover));
    }

    @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
      &:hover { background-color: rgb(var(--v-theme-hover)); }
    }

    &.bg-secondary:hover { background-color: #C4126B; }
  }

  .secondary-button:hover {
    background-color: rgb(var(--v-theme-hover));
  }
}

</style>
